import {Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {AppNumberPipe} from '@frontend/shared';
import {CommonModule} from '@angular/common';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {TableModule} from 'primeng/table';
import {Button, ButtonModule} from 'primeng/button';
import {Tooltip, TooltipModule} from 'primeng/tooltip';
import {
  DirectusService,
  DirectusTranslation,
  DirectusTranslationPipe,
  EvaluationCriteria,
  EvaluationSubCriteriaDirectus,
  HrTool,
} from '@frontend/directus';
import {SubCriteriaItem} from '../../../model/sub-criteria.interfaces';
import {
  DemonstrationEvaluationControllerService,
  EvaluationCriteriaControllerService,
  EvaluationCriteriaResponse,
  HrToolsControllerService,
  ProjectRoleType,
  ProjectStepType,
  ProjectUserControllerService,
  ParticipatingToolControllerService,
  ProviderTenderInvitationOfferOverviewResponse,
  ProjectSettingControllerService,
  ProjectSettingResponse,
  TenderPhaseType,
  RfqControllerService,
  ProviderResultsSummaryRequest,
  ProviderRef,
  AttributeResult,
  ProviderAttributeScore,
  EvaluationSubCriteriaImportanceScore,
} from '@frontend/shared-api';
import {firstValueFrom} from 'rxjs';
import {
  ProviderScoreCalculationResult,
  ProviderScoreCalculationService,
} from '../../../services/provider-score-calculation.service';
import {ProviderTotalScoreCalculationService} from '../../../services/provider-total-score-calculation.service';
import {
  RfdProviderScore,
  RfdScoreCalculationService,
} from '../../../services/rfd-score-calculation.service';
import {ProjectStepStateService} from '../../../../pages/customer/project-sequence/project-step-state.service';
import {SharedModule} from 'primeng/api';
import {SubcriteriaScoresModalComponent} from '../../subcriteria-scores-modal/subcriteria-scores-modal.component';
import {SubcriteriaAttributeScoresModalComponent} from '../../subcriteria-attribute-scores-modal/subcriteria-attribute-scores-modal.component';
import {RfdAgendaItemScoresModalComponent} from '../../dialog/rfd-agenda-item-scores-modal/rfd-agenda-item-scores-modal.component';
import {BaseParticipatingTool} from '../invitation-table/invitation-table.types';
import {
  CriteriaRow,
  ProviderColumn,
  ProviderResult,
  SubCriteriaRow,
} from './provider-results-table.types';
import {EnrichedEvaluationDataResponse} from '../../../../pages/customer/project-sequence/chapter/chapter-7-rfd/step-7-5-provider-evaluation/evaluation-models';
import {getMaxScore} from '../../../utils/rfd-evaluation-score.utils';
import {buildEnrichedRfdEvaluationData} from '../../../services/rfd-evaluation-data.service';
import {SCORE_COLORS} from '../../../constants/provider-results.constants';
import {createScoreChartData, getChartColor} from '../../../utils/score-chart.utils';
import {RfiIndividualResultsModalComponent} from '../../rfi-individual-results-modal/rfi-individual-results-modal.component';
import {RfpIndividualResultsModalComponent} from '../../rfp-individual-results-modal/rfp-individual-results-modal.component';
import {buildRfpIndividualResults} from '../../rfp-individual-results-modal/rfp-individual-results.calc';
import {RfpIndividualResults} from '../../rfp-individual-results-modal/rfp-individual-results.types';
import {ProviderScoreCellComponent} from './provider-score-cell/provider-score-cell.component';
import {ProviderScoreDisplay} from './provider-score-cell/provider-score-cell.types';
import {IndividualSubCriteriaScore} from '../../rfi-individual-results-modal/rfi-individual-results-modal.types';
import {INDIVIDUAL_RESULT_SC_NUMBERS} from '../../rfi-individual-results-modal/rfi-individual-results.calc';
import {buildAiSummaryKpis, buildAiSummaryTotals} from './provider-results-ai-summary-context';
import {RfqProviderResultsService} from './services/rfq-provider-results.service';
import {RfqCriteriaTableData} from './services/rfq-provider-results.types';
import {NoDataComponent} from '../../no-data/no-data.component';
import {ProviderResultsAiSummaryComponent} from '../../provider-results-ai-summary/provider-results-ai-summary.component';
import {LoadingComponent} from '../../loading/loading.component';
import {ConfirmationDialogService} from '../../dialog/confirmation/confirmation-dialog.service';
import {TenderPhaseCompletionUtils} from '../../../../phase/tender-phase-completion.util';

export type ProviderResultsDisplayMode = 'table' | 'heatmap';

interface CriteriaItem {
  ecNumber: number;
  directusId?: string;
  translations?: DirectusTranslation[];
  includedSubCriteria: SubCriteriaItem[];
}

interface TotalsData {
  providerResultsByHrToolId: Map<string, ProviderResult>;
  maxValue: number;
}

@Component({
  selector: 'app-provider-results-table',
  imports: [
    AppNumberPipe,
    CommonModule,
    TranslocoPipe,
    TableModule,
    Button,
    Tooltip,
    ButtonModule,
    TooltipModule,
    SubcriteriaScoresModalComponent,
    SubcriteriaAttributeScoresModalComponent,
    RfdAgendaItemScoresModalComponent,
    SharedModule,
    NoDataComponent,
    ProviderResultsAiSummaryComponent,
    LoadingComponent,
    RfiIndividualResultsModalComponent,
    RfpIndividualResultsModalComponent,
    ProviderScoreCellComponent,
  ],
  templateUrl: './provider-results-table.component.html',
  styleUrls: ['./provider-results-table.component.scss'],
})
export class ProviderResultsTableComponent implements OnInit {
  private static readonly RFD_DEMONSTRATION_EC_NUMBER = 8;
  private static readonly RFQ_EC_NUMBER = 9;
  private static readonly RFQ_SUBCRITERIA_NUMBERS = [91, 92, 93, 94];

  private readonly providerScoreCalculationService = inject(ProviderScoreCalculationService);
  private readonly providerTotalScoreCalculationService = inject(
    ProviderTotalScoreCalculationService,
  );
  private readonly rfdScoreCalculationService = inject(RfdScoreCalculationService);
  private readonly demonstrationEvaluationController = inject(
    DemonstrationEvaluationControllerService,
  );
  private readonly projectUserController = inject(ProjectUserControllerService);
  private readonly hrToolsController = inject(HrToolsControllerService);
  private readonly participatingToolController = inject(ParticipatingToolControllerService);
  protected readonly stepStateService = inject(ProjectStepStateService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly translocoService = inject(TranslocoService);
  private readonly directusService = inject(DirectusService);
  private readonly directusTranslationPipe = new DirectusTranslationPipe();
  private readonly evaluationCriteriaController = inject(EvaluationCriteriaControllerService);
  private readonly projectSettingController = inject(ProjectSettingControllerService);
  private readonly rfqController = inject(RfqControllerService);
  private readonly rfqProviderResultsService = inject(RfqProviderResultsService);
  private readonly currentLang = this.translocoService.getActiveLang();

  protected readonly TenderPhaseType = TenderPhaseType;

  readonly projectId = input.required<number>();
  readonly phase = input<TenderPhaseType>(TenderPhaseType.RFI);
  readonly displayMode = input<ProviderResultsDisplayMode>('table');
  readonly allowSubcriteriaAttributeModal = input(true);
  readonly allowViewResultsModal = input(true);
  readonly showLegend = input(true);
  readonly showAiSummary = input(true);
  readonly showCompletePhaseButton = input(true);
  readonly showScoreCircle = input(true);
  readonly scoreBars = input(false);
  readonly allowIndividualResultsModal = input(false);
  protected readonly scoreDisplay = computed((): ProviderScoreDisplay => {
    if (this.scoreBars()) {
      return 'bar';
    }
    return this.showScoreCircle() ? 'pie' : 'none';
  });
  readonly includedHrToolDirectusIds = input<string[]>();

  readonly loading = signal(false);
  protected readonly scoreColors = SCORE_COLORS;
  readonly calculationResult = signal<ProviderScoreCalculationResult | null>(null);
  readonly hrTools = signal<HrTool[]>([]);
  readonly directusCriteria = signal<EvaluationCriteria[]>([]);
  readonly directusSubCriteria = signal<EvaluationSubCriteriaDirectus[]>([]);
  readonly subcriteriaScoresModalVisible = signal(false);
  protected readonly individualResultsModalVisible = signal(false);
  protected readonly individualResultsProvider = signal<ProviderColumn | null>(null);
  readonly selectedTool = signal<HrTool | null>(null);
  readonly subcriteriaAttributeScoresModalVisible = signal(false);
  readonly selectedSubCriteria = signal<SubCriteriaRow | null>(null);
  readonly isExponentialScale = signal(false);
  readonly projectSettings = signal<ProjectSettingResponse | undefined>(undefined);
  readonly rfqInvitationOverviews = signal<ProviderTenderInvitationOfferOverviewResponse[]>([]);
  readonly rfdScoresByToolId = signal<Map<string, RfdProviderScore>>(new Map());
  readonly rfdEvaluationData = signal<EnrichedEvaluationDataResponse | null>(null);
  readonly rfdMaxScore = computed(() => getMaxScore(this.isExponentialScale()));
  private readonly phaseCompletable = signal(false);

  readonly ecs = signal<EvaluationCriteriaResponse[]>([]);

  protected readonly totalLabelKey = 'project.steps.providerResults.total.rfp';
  protected readonly maxValueLabelKey = 'project.steps.providerResults.maxValue.rfp';
  protected readonly isRfiPhase = computed(() => this.phase() === TenderPhaseType.RFI);
  protected readonly isRfdPhase = computed(() => this.phase() === TenderPhaseType.RFD);
  protected readonly isRfqPhase = computed(() => this.phase() === TenderPhaseType.RFQ);

  chartOptions: any = {
    animation: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
    },
    interaction: {
      mode: undefined,
    },
    events: [],
  };

  ngOnInit(): void {
    void this.loadData();
  }

  protected readonly selectedParticipatingTool = computed((): BaseParticipatingTool | undefined => {
    const tool = this.selectedTool();
    if (!tool) return undefined;

    const lang = this.translocoService.getActiveLang();
    const translatedName = this.directusTranslationPipe.transform(
      tool.translations,
      lang,
      tool.name,
    );

    return {
      id: tool.id.toString(),
      translatedName: translatedName || tool.name,
      toolProvider: tool.toolProvider,
    };
  });

  protected readonly allSubCriteria = computed(() => {
    return this.isRfqPhase()
      ? this.getRfqAllSubCriteria()
      : this.getDefaultAllSubCriteria(this.calculationResult());
  });

  protected readonly includedCriteria = computed((): CriteriaItem[] => {
    return this.isRfqPhase()
      ? this.getRfqIncludedCriteria()
      : this.getDefaultIncludedCriteria(this.calculationResult());
  });

  protected readonly providerColumns = computed((): ProviderColumn[] => {
    return this.isRfqPhase()
      ? this.getRfqProviderColumns()
      : this.getDefaultProviderColumns(
          this.calculationResult(),
          this.hrTools(),
          this.allSubCriteria(),
        );
  });

  protected readonly showCompletePhase = computed<boolean>(() => {
    const activeStep = this.stepStateService.activeStep();
    const isLocked = activeStep
      ? this.stepStateService.isStepLocked(activeStep.chapterId, activeStep.stepId)
      : false;

    return (
      this.showCompletePhaseButton() &&
      this.phaseCompletable() &&
      !this.stepStateService.isCurrentStepFinished() &&
      this.providerColumns().length > 0 &&
      !isLocked
    );
  });

  protected readonly noDataInfo = computed(
    (): {icon: string; titleKey: string; messageKey: string} | null => {
      if (this.loading() || this.providerColumns().length > 0) {
        return null;
      }

      const result = this.calculationResult();
      const tools = this.hrTools();

      const rfqInfo = this.getRfqNoDataInfo();
      if (rfqInfo) {
        return rfqInfo;
      }

      if (!result || result.allScores.length === 0) {
        return {
          icon: 'pi pi-clock text-4xl text-gray-400 mb-4',
          titleKey: 'project.steps.providerResults.previousStepIncompleteTitle',
          messageKey: 'project.steps.providerResults.previousStepIncompleteMessage',
        };
      }

      if (tools.length === 0) {
        return {
          icon: 'pi pi-users text-4xl text-gray-400 mb-4',
          titleKey: 'project.steps.providerResults.noProvidersTitle',
          messageKey: 'project.steps.providerResults.noProvidersMessage',
        };
      }

      return {
        icon: 'pi pi-info-circle text-4xl text-gray-400 mb-4',
        titleKey: 'project.steps.providerResults.noDataTitle',
        messageKey: 'project.steps.providerResults.noDataMessage',
      };
    },
  );

  protected readonly tableData = computed((): CriteriaRow[] => {
    const providers = this.providerColumns();
    if (providers.length === 0) {
      return [];
    }

    if (this.isRfqPhase()) {
      const rfqCriteriaData = this.getRfqCriteriaTableData();
      if (!rfqCriteriaData) {
        return [];
      }

      return this.rfqProviderResultsService.buildRfqTableData(
        rfqCriteriaData,
        providers,
        this.rfqInvitationOverviews(),
        this.projectSettings(),
        this.isExponentialScale(),
        (value, maxValue) => this.createProviderResult(value, maxValue),
      );
    }

    const result = this.calculationResult();
    const ecs = this.ecs();
    if (!result) {
      return [];
    }

    const rows: CriteriaRow[] = [];

    this.includedCriteria().forEach((criteria) => {
      // Build subcriteria rows
      const subCriteriaRows: SubCriteriaRow[] = criteria.includedSubCriteria.map((subCriteria) => {
        const providerDataByHrToolId = new Map<string, ProviderResult>();
        let maxValue = 0;

        // Get weight from backend
        const ec = ecs.find((e) => e.ecNumber === criteria.ecNumber);
        const sc = ec?.subCriteria?.find((s) => s.scNumber === subCriteria.scNumber);
        const scWeight = sc?.weight;

        providers.forEach((provider) => {
          const providerResult = this.getSubCriteriaProviderResult(
            result,
            subCriteria,
            provider.hrToolDirectusId,
          );
          if (!providerResult) return;

          providerDataByHrToolId.set(provider.hrToolDirectusId, providerResult);
          if (this.isRfdPhase()) {
            maxValue = this.rfdMaxScore();
          } else {
            const denominator = providerResult.percentage / 100;
            maxValue =
              denominator > 0
                ? this.roundToTwoDecimals(providerResult.value / denominator)
                : maxValue;
          }
        });

        const translatedLabel =
          this.directusTranslationPipe.transform(subCriteria.translations, this.currentLang, '') ??
          '';

        return {
          scNumber: subCriteria.scNumber,
          directusId: subCriteria.directusId,
          translatedLabel,
          maxValue,
          weight: scWeight,
          providerResultsByHrToolId: providerDataByHrToolId,
        };
      });

      // Build criteria row with aggregated results
      const providerData = new Map<string, ProviderResult>();
      let maxValue = 0;

      // Get weight from backend
      const ec = ecs.find((e) => e.ecNumber === criteria.ecNumber);
      const ecWeight = ec?.weight;

      providers.forEach((provider) => {
        const providerResult = this.getCriteriaProviderResult(
          result,
          criteria,
          provider.hrToolDirectusId,
          ec,
        );
        if (!providerResult) return;

        providerData.set(provider.hrToolDirectusId, providerResult);
        if (this.isRfdPhase()) {
          maxValue = this.rfdMaxScore();
        } else {
          const denominator = providerResult.percentage / 100;
          maxValue =
            denominator > 0
              ? this.roundToTwoDecimals(providerResult.value / denominator)
              : maxValue;
        }
      });

      const translatedLabel =
        this.directusTranslationPipe.transform(criteria.translations, this.currentLang, '') ?? '';

      rows.push({
        ecNumber: criteria.ecNumber,
        translatedLabel,
        maxValue,
        weight: ecWeight,
        directusId: criteria.directusId,
        subCriteria: subCriteriaRows,
        providerResultsByHrToolId: providerData,
        expanded: false, // Initially collapsed
      });
    });

    return rows;
  });

  // Context for the AI results summary (SCRUM-222): the resolved provider names,
  // per-sub-criteria scores and Kano weighting the LLM needs. Null until the
  // table data is ready so the summary component holds off its request.
  readonly aiSummaryRequest = computed<ProviderResultsSummaryRequest | null>(() => {
    const providers = this.providerColumns();
    const rows = this.tableData();
    const total = this.totalRow();
    if (providers.length === 0 || rows.length === 0 || !total) {
      return null;
    }

    const importanceBySubCriteria = this.buildImportanceBySubCriteria();
    const providerIds = providers.map((provider) => provider.hrToolDirectusId);
    const calculationResult = this.calculationResult();

    const providerRefs: ProviderRef[] = providers.map((provider) => ({
      providerId: provider.hrToolDirectusId,
      providerName: provider.translatedName,
    }));

    const attributes: AttributeResult[] = [];
    rows.forEach((criteria) => {
      criteria.subCriteria.forEach((subCriteria) => {
        const scores: ProviderAttributeScore[] = [];
        subCriteria.providerResultsByHrToolId.forEach((result, hrToolDirectusId) => {
          scores.push({
            providerId: hrToolDirectusId,
            score: result.value,
            maxScore: subCriteria.maxValue,
          });
        });
        if (scores.length === 0) {
          return;
        }
        attributes.push({
          criterionName: criteria.translatedLabel,
          criterionWeight: criteria.weight,
          subCriterionWeight: subCriteria.weight,
          attributeName: subCriteria.translatedLabel,
          importance: importanceBySubCriteria.get(subCriteria.directusId),
          scores,
        });
      });
    });

    if (attributes.length === 0) {
      return null;
    }

    return {
      phase: this.phase(),
      language: this.currentLang,
      providers: providerRefs,
      attributes,
      totals: buildAiSummaryTotals(providerIds, total.maxValue, total.providerResultsByHrToolId),
      kpis:
        this.isRfiPhase() && calculationResult
          ? buildAiSummaryKpis(calculationResult.requestedCriteria, providerIds)
          : undefined,
    };
  });

  protected readonly totalRow = computed(() => {
    const providers = this.providerColumns();
    const dataRows = this.tableData();
    const ecs = this.ecs();

    if (providers.length === 0) {
      return null;
    }

    if (this.isRfdPhase()) {
      return this.buildTotalRow(this.calculateRfdTotals(providers), dataRows);
    }

    if (this.isRfqPhase()) {
      return this.buildTotalRow(this.calculateRfqTotals(providers, dataRows), dataRows);
    }

    const result = this.calculationResult();
    if (!result) {
      return null;
    }

    return this.buildTotalRow(this.calculateDefaultTotals(result, providers, ecs), dataRows);
  });

  protected toggleRowExpansion(row: CriteriaRow): void {
    row.expanded = !row.expanded;
  }

  protected isHeatmapMode(): boolean {
    return this.displayMode() === 'heatmap';
  }

  private roundToTwoDecimals(value: number): number {
    return Math.round(value * 100) / 100;
  }

  // Maps each sub-criteria (by directusId) to its most decision-relevant Kano
  // weighting, taken from the strongest contributor importance across its
  // attributes. The enum is declared in decreasing-importance order, so the
  // lowest index wins.
  private buildImportanceBySubCriteria(): Map<string, EvaluationSubCriteriaImportanceScore> {
    const result = this.calculationResult();
    const importanceBySubCriteria = new Map<string, EvaluationSubCriteriaImportanceScore>();
    if (!result) {
      return importanceBySubCriteria;
    }

    const importanceRank = Object.values(EvaluationSubCriteriaImportanceScore);
    result.requestedCriteria.forEach((criteria) => {
      (criteria.subCriteria ?? []).forEach((subCriteria) => {
        let strongest: EvaluationSubCriteriaImportanceScore | undefined;
        let strongestRank = Number.MAX_SAFE_INTEGER;
        (subCriteria.attributes ?? []).forEach((attribute) => {
          (attribute.importanceScores ?? []).forEach((info) => {
            const rank = importanceRank.indexOf(info.importanceScore);
            if (rank >= 0 && rank < strongestRank) {
              strongestRank = rank;
              strongest = info.importanceScore;
            }
          });
        });
        if (strongest) {
          importanceBySubCriteria.set(subCriteria.directusId, strongest);
        }
      });
    });

    return importanceBySubCriteria;
  }

  private getRfqAllSubCriteria(): SubCriteriaItem[] {
    return this.includedCriteria().flatMap((criteria) => criteria.includedSubCriteria);
  }

  private getDefaultAllSubCriteria(
    result: ProviderScoreCalculationResult | null,
  ): SubCriteriaItem[] {
    if (!result) return [];

    const allSubs: SubCriteriaItem[] = [];
    result.subCriteriaByEc.forEach((ecSubCriteria) => {
      allSubs.push(...ecSubCriteria);
    });
    return allSubs;
  }

  private getRfqIncludedCriteria(): CriteriaItem[] {
    const ec = this.ecs().find(
      (item) => item.ecNumber === ProviderResultsTableComponent.RFQ_EC_NUMBER,
    );
    if (!ec) return [];

    const directusCriterion = this.directusCriteria().find(
      (criteria) => criteria.ecNumber === ProviderResultsTableComponent.RFQ_EC_NUMBER,
    );
    const includedSubCriteria = (ec.subCriteria ?? [])
      .filter(
        (subCriteria) =>
          subCriteria.included === true &&
          ProviderResultsTableComponent.RFQ_SUBCRITERIA_NUMBERS.includes(subCriteria.scNumber),
      )
      .map((subCriteria) => {
        const directusSub = this.directusSubCriteria().find(
          (item) => item.id === subCriteria.directusId,
        );
        return {
          id: subCriteria.id,
          directusId: subCriteria.directusId,
          scNumber: subCriteria.scNumber,
          included: true,
          isSaving: false,
          translations: directusSub?.translations,
        } as SubCriteriaItem;
      })
      .toSorted((a, b) => a.scNumber - b.scNumber);

    if (includedSubCriteria.length === 0) {
      return [];
    }

    return [
      {
        ecNumber: ProviderResultsTableComponent.RFQ_EC_NUMBER,
        directusId: directusCriterion?.id ?? ec.directusId,
        translations: directusCriterion?.translations,
        includedSubCriteria,
      },
    ];
  }

  private getDefaultIncludedCriteria(
    result: ProviderScoreCalculationResult | null,
  ): CriteriaItem[] {
    if (!result) return [];

    const criteriaMap = new Map<number, CriteriaItem>();
    result.subCriteriaByEc.forEach((subCriteria, ecNumber) => {
      const includedSubs = subCriteria.filter((sc) => sc.included === true);
      if (includedSubs.length === 0) return;

      const criteriaData = result.requestedCriteria.find(
        (criterion) => criterion.ecNumber === ecNumber,
      );
      const directusCriterion = this.directusCriteria().find(
        (criterion) => criterion.id === criteriaData?.directusId,
      );
      criteriaMap.set(ecNumber, {
        ecNumber,
        directusId: criteriaData?.directusId,
        translations: directusCriterion?.translations,
        includedSubCriteria: includedSubs.toSorted((a, b) => a.scNumber - b.scNumber),
      });
    });

    const criteria = Array.from(criteriaMap.values()).sort((a, b) => a.ecNumber - b.ecNumber);
    if (!this.isRfdPhase()) {
      return criteria;
    }

    return criteria.filter(
      (criterion) =>
        criterion.ecNumber === ProviderResultsTableComponent.RFD_DEMONSTRATION_EC_NUMBER,
    );
  }

  private getRfqProviderColumns(): ProviderColumn[] {
    const columns = this.rfqProviderResultsService
      .buildProviderColumns(this.rfqInvitationOverviews(), this.hrTools())
      .map((column) => {
        const translatedName =
          this.directusTranslationPipe.transform(
            column.hrTool.translations,
            this.currentLang,
            column.hrTool.name,
          ) || column.translatedName;
        return {...column, translatedName};
      });
    return this.applyIncludedToolsFilterAndOrder(columns);
  }

  private applyIncludedToolsFilterAndOrder(columns: ProviderColumn[]): ProviderColumn[] {
    const allowedToolIds = this.includedHrToolDirectusIds();
    if (!allowedToolIds || allowedToolIds.length === 0) {
      return columns;
    }
    const byToolId = new Map(columns.map((column) => [column.hrToolDirectusId, column]));
    return allowedToolIds
      .map((toolId) => byToolId.get(toolId))
      .filter((column): column is ProviderColumn => column !== undefined);
  }

  private getDefaultProviderColumns(
    result: ProviderScoreCalculationResult | null,
    tools: HrTool[],
    subCriteria: SubCriteriaItem[],
  ): ProviderColumn[] {
    if (!result || tools.length === 0 || subCriteria.length === 0) {
      return [];
    }

    const uniqueToolIds = this.isRfdPhase()
      ? new Set(this.rfdScoresByToolId().keys())
      : new Set(result.allScores.map((score) => score.hrToolDirectusId));

    const columns = Array.from(uniqueToolIds)
      .map((toolId) => {
        const hrTool = tools.find((tool) => tool.id.toString() === toolId);
        if (!hrTool) return undefined;

        const translatedName =
          this.directusTranslationPipe.transform(
            hrTool.translations,
            this.currentLang,
            hrTool.name,
          ) || hrTool.name;
        return {hrToolDirectusId: toolId, hrTool, translatedName} as ProviderColumn;
      })
      .filter((column): column is ProviderColumn => column !== undefined);

    return this.applyIncludedToolsFilterAndOrder(columns);
  }

  private getRfqNoDataInfo(): {icon: string; titleKey: string; messageKey: string} | null {
    if (!this.isRfqPhase()) return null;
    if (this.rfqInvitationOverviews().length > 0) {
      if (this.providerColumns().length === 0) {
        return {
          icon: 'pi pi-info-circle text-4xl text-gray-400 mb-4',
          titleKey: 'project.steps.providerResults.noDataTitle',
          messageKey: 'project.steps.providerResults.noDataMessage',
        };
      }
      return null;
    }

    return {
      icon: 'pi pi-users text-4xl text-gray-400 mb-4',
      titleKey: 'project.steps.providerResults.noProvidersTitle',
      messageKey: 'project.steps.providerResults.noProvidersMessage',
    };
  }

  private calculateRfdTotals(providers: ProviderColumn[]): TotalsData {
    const providerResultsByHrToolId = new Map<string, ProviderResult>();
    let maxValue = 0;

    providers.forEach((provider) => {
      const rfdScore = this.rfdScoresByToolId().get(provider.hrToolDirectusId);
      if (!rfdScore) return;

      const maxScore = this.rfdMaxScore();
      providerResultsByHrToolId.set(
        provider.hrToolDirectusId,
        this.createProviderResult(rfdScore.overall, maxScore),
      );
      if (maxValue === 0) {
        maxValue = maxScore;
      }
    });

    return {providerResultsByHrToolId, maxValue};
  }

  private calculateRfqTotals(providers: ProviderColumn[], dataRows: CriteriaRow[]): TotalsData {
    const providerResultsByHrToolId = new Map<string, ProviderResult>();
    let maxValue = 0;

    providers.forEach((provider) => {
      const criteriaWithResult = dataRows
        .map((row) => ({
          row,
          result: row.providerResultsByHrToolId.get(provider.hrToolDirectusId),
          weight: row.weight ?? 1.0,
        }))
        .filter((item) => item.result !== undefined);

      if (criteriaWithResult.length !== dataRows.length || criteriaWithResult.length === 0) {
        return;
      }

      const total = criteriaWithResult.reduce(
        (sum, item) => sum + (item.result?.value ?? 0) * item.weight,
        0,
      );
      const maxScore = criteriaWithResult.reduce(
        (sum, item) => sum + this.roundToTwoDecimals((item.row.maxValue ?? 0) * item.weight),
        0,
      );

      providerResultsByHrToolId.set(
        provider.hrToolDirectusId,
        this.createProviderResult(total, maxScore),
      );
      if (maxValue === 0) {
        maxValue = maxScore;
      }
    });

    return {providerResultsByHrToolId, maxValue};
  }

  private calculateDefaultTotals(
    result: ProviderScoreCalculationResult,
    providers: ProviderColumn[],
    ecs: EvaluationCriteriaResponse[],
  ): TotalsData {
    const providerResultsByHrToolId = new Map<string, ProviderResult>();
    let maxValue = 0;
    const totalsByProvider =
      this.providerTotalScoreCalculationService.calculateProviderTotalsFromResult(result, ecs);

    providers.forEach((provider) => {
      const providerTotals = totalsByProvider.get(provider.hrToolDirectusId);
      if (!providerTotals) return;

      providerResultsByHrToolId.set(
        provider.hrToolDirectusId,
        this.createProviderResult(providerTotals.total, providerTotals.maxPossibleScore),
      );

      if (maxValue === 0) {
        maxValue = this.roundToTwoDecimals(providerTotals.maxPossibleScore);
      }
    });

    return {providerResultsByHrToolId, maxValue};
  }

  private buildTotalRow(
    totalsData: TotalsData,
    dataRows: CriteriaRow[],
  ): {
    translatedLabel: string;
    maxValue: number;
    weight: number;
    providerResultsByHrToolId: Map<string, ProviderResult>;
  } {
    const totalWeight = dataRows.reduce((sum, row) => sum + (row.weight ?? 0), 0);
    return {
      translatedLabel: this.translocoService.translate(this.totalLabelKey),
      maxValue: totalsData.maxValue,
      weight: totalWeight,
      providerResultsByHrToolId: totalsData.providerResultsByHrToolId,
    };
  }

  private async loadRfqInvitationOverviews(): Promise<
    ProviderTenderInvitationOfferOverviewResponse[]
  > {
    if (!this.isRfqPhase()) {
      return [];
    }

    return await firstValueFrom(
      this.rfqController.getInvitationOfferOverviews({projectId: this.projectId()}),
    ).catch(() => []);
  }

  private async loadCalculationResult(): Promise<ProviderScoreCalculationResult | null> {
    if (this.isRfqPhase()) {
      return null;
    }

    return await this.providerScoreCalculationService.calculateProviderScores(
      this.projectId(),
      this.phase(),
    );
  }

  private getSubCriteriaProviderResult(
    result: ProviderScoreCalculationResult,
    subCriteria: SubCriteriaItem,
    hrToolDirectusId: string,
  ): ProviderResult | null {
    if (this.isRfdPhase()) {
      const rfdScore = this.rfdScoresByToolId().get(hrToolDirectusId);
      const scoreValue = rfdScore?.subcriteriaScores.get(subCriteria.directusId);
      if (scoreValue == null) return null;
      return this.createProviderResult(scoreValue, this.rfdMaxScore());
    }

    const fieldCombination = this.providerScoreCalculationService.getFieldCombination(
      result.fieldCombinations,
      subCriteria.scNumber,
      hrToolDirectusId,
    );
    if (!fieldCombination) return null;

    return this.createProviderResult(
      fieldCombination.normalizedScore,
      fieldCombination.maxPossibleScore,
    );
  }

  private getCriteriaProviderResult(
    result: ProviderScoreCalculationResult,
    criteria: CriteriaItem,
    hrToolDirectusId: string,
    ec: EvaluationCriteriaResponse | undefined,
  ): ProviderResult | null {
    if (this.isRfdPhase()) {
      const rfdScore = this.rfdScoresByToolId().get(hrToolDirectusId);
      if (!rfdScore) return null;
      return this.createProviderResult(rfdScore.overall, this.rfdMaxScore());
    }

    let sum = 0;
    let maxSum = 0;
    criteria.includedSubCriteria.forEach((subCriteria) => {
      const fieldCombination = this.providerScoreCalculationService.getFieldCombination(
        result.fieldCombinations,
        subCriteria.scNumber,
        hrToolDirectusId,
      );

      if (!fieldCombination) return;
      const sc = ec?.subCriteria?.find((item) => item.scNumber === subCriteria.scNumber);
      const weight = sc?.weight ?? 1.0;
      sum += fieldCombination.normalizedScore * weight;
      maxSum += fieldCombination.maxPossibleScore * weight;
    });

    return this.createProviderResult(sum, maxSum);
  }

  private createProviderResult(value: number, maxValue: number): ProviderResult {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return {
      value,
      percentage,
      chartData: this.createChartData(percentage),
    };
  }

  createChartData(percent: number | undefined) {
    return createScoreChartData(percent);
  }

  getChartColor(percent: number) {
    return getChartColor(percent);
  }

  private async loadData(): Promise<void> {
    this.loading.set(true);
    try {
      const [hrTools, directusCriteria, directusSubCriteria, ecs, projectSettings] =
        (await Promise.all([
          this.loadHrTools(),
          firstValueFrom(this.directusService.loadEvaluationCriteria()),
          firstValueFrom(this.directusService.loadSubCriteria()),
          firstValueFrom(
            this.evaluationCriteriaController.getEvaluationCriteriaByProject({
              projectId: this.projectId(),
            }),
          ),
          firstValueFrom(
            this.projectSettingController.getProjectSetting({projectId: this.projectId()}),
          ).catch(() => null),
        ])) as [
          HrTool[],
          EvaluationCriteria[],
          EvaluationSubCriteriaDirectus[],
          EvaluationCriteriaResponse[],
          ProjectSettingResponse | null,
        ];
      const rfqInvitationOverviews = await this.loadRfqInvitationOverviews();
      const calculationResult = await this.loadCalculationResult();
      const phaseCompletable = await this.loadPhaseCompletable().catch(() => false);

      this.hrTools.set(hrTools);
      this.calculationResult.set(calculationResult);
      this.directusCriteria.set(directusCriteria);
      this.directusSubCriteria.set(directusSubCriteria);
      this.ecs.set(ecs);
      this.isExponentialScale.set(Boolean(projectSettings?.isExponential));
      this.projectSettings.set(projectSettings ?? undefined);
      this.rfqInvitationOverviews.set(rfqInvitationOverviews);
      this.phaseCompletable.set(phaseCompletable);
      if (this.phase() === TenderPhaseType.RFD) {
        const rfdResult = await this.loadRfdProviderScores(Boolean(projectSettings?.isExponential));
        this.rfdScoresByToolId.set(rfdResult.scoresByToolId);
        this.rfdEvaluationData.set(rfdResult.enrichedData);
      } else {
        this.rfdScoresByToolId.set(new Map());
        this.rfdEvaluationData.set(null);
      }
    } catch (error) {
      console.error('Error loading provider results data:', error);
    } finally {
      this.loading.set(false);
    }
  }

  private async loadPhaseCompletable(): Promise<boolean> {
    if (!this.showCompletePhaseButton() || this.isRfdPhase()) {
      return false;
    }

    const participatingTools = await firstValueFrom(
      this.participatingToolController.getAllParticipatingTools({
        projectId: this.projectId(),
        phase: this.phase(),
      }),
    );

    return TenderPhaseCompletionUtils.canCompletePhase(
      participatingTools,
      this.phase(),
      new Date(),
    );
  }

  private async loadRfdProviderScores(isExponentialScale: boolean): Promise<{
    scoresByToolId: Map<string, RfdProviderScore>;
    enrichedData: EnrichedEvaluationDataResponse;
  }> {
    const [evaluationDataResponse, contributors, allSubcriteria, agenda] = await Promise.all([
      firstValueFrom(
        this.demonstrationEvaluationController.getEvaluationData({projectId: this.projectId()}),
      ),
      firstValueFrom(
        this.projectUserController.getProjectUsersWithDetails({projectId: this.projectId()}),
      ),
      firstValueFrom(this.directusService.loadSubCriteria()),
      firstValueFrom(this.directusService.loadDemonstrationAgenda()),
    ]);

    const contributorIds = contributors
      .filter(
        (user) =>
          (user.roles.includes(ProjectRoleType.CONTRIBUTOR) &&
            (user.accessibleSteps.includes(ProjectStepType.RFD_PROVIDER_EVALUATION) ||
              user.accessibleSteps.includes(ProjectStepType.RFD_PROVIDER_RESULTS))) ||
          user.roles.includes(ProjectRoleType.PROJECT_MANAGER),
      )
      .map((user) => user.userId);

    const rfdCriteriaDirectusId = this.directusCriteria().find(
      (criteria) => criteria.ecNumber === ProviderResultsTableComponent.RFD_DEMONSTRATION_EC_NUMBER,
    )?.id;
    const enrichedData = buildEnrichedRfdEvaluationData(
      evaluationDataResponse,
      allSubcriteria,
      agenda,
      {
        criteriaDirectusId: rfdCriteriaDirectusId,
      },
    );
    const scores = this.rfdScoreCalculationService.calculateProviderScores(
      enrichedData,
      contributorIds,
      isExponentialScale,
    );
    return {
      scoresByToolId: new Map(scores.map((score) => [score.hrToolDirectusId, score])),
      enrichedData,
    };
  }

  private async loadHrTools(): Promise<HrTool[]> {
    const registeredHrToolIds = await firstValueFrom(this.hrToolsController.getRegisteredHrTools());
    return await firstValueFrom(this.directusService.loadHrToolsByIds(registeredHrToolIds));
  }

  protected readonly individualSubCriteriaScores = computed((): IndividualSubCriteriaScore[] => {
    const provider = this.individualResultsProvider();
    if (!provider) {
      return [];
    }
    const subCriteriaRows = this.tableData().flatMap((criteriaRow) => criteriaRow.subCriteria);
    return Object.values(INDIVIDUAL_RESULT_SC_NUMBERS).map((scNumber) => {
      const subCriteriaRow = subCriteriaRows.find((row) => row.scNumber === scNumber);
      const directusSubCriteria = this.directusSubCriteria().find(
        (sub) => sub.sc_number === scNumber,
      );
      return {
        scNumber,
        label:
          this.directusTranslationPipe.transform(
            directusSubCriteria?.translations,
            this.currentLang,
            subCriteriaRow?.translatedLabel ?? '',
          ) ?? '',
        percentage:
          subCriteriaRow?.providerResultsByHrToolId.get(provider.hrToolDirectusId)?.percentage ??
          null,
        evaluated: subCriteriaRow !== undefined,
      };
    });
  });

  protected readonly rfpIndividualResults = computed((): RfpIndividualResults | null => {
    const provider = this.individualResultsProvider();
    const total = this.totalRow();
    if (!provider || this.isRfiPhase()) {
      return null;
    }
    return buildRfpIndividualResults({
      rows: this.tableData().map((row) => ({
        ecNumber: row.ecNumber,
        label: row.translatedLabel,
        weight: row.weight,
        maxValue: row.maxValue,
        scores: row.providerResultsByHrToolId,
        subCriteria: row.subCriteria.map((subRow) => ({
          scNumber: subRow.scNumber,
          label: subRow.translatedLabel,
          weight: subRow.weight,
          scores: subRow.providerResultsByHrToolId,
        })),
      })),
      totals: total?.providerResultsByHrToolId ?? new Map(),
      providerIds: this.providerColumns().map((column) => column.hrToolDirectusId),
      providerId: provider.hrToolDirectusId,
    });
  });

  protected onIndividualResultsClick(provider: ProviderColumn): void {
    this.individualResultsProvider.set(provider);
    this.individualResultsModalVisible.set(true);
  }

  protected onViewResultsClick(provider: ProviderColumn): void {
    this.selectedTool.set(provider.hrTool);
    this.subcriteriaScoresModalVisible.set(true);
  }

  private getRfqCriteriaTableData(): RfqCriteriaTableData | undefined {
    const rfqCriteria = this.includedCriteria().find(
      (criteria) => criteria.ecNumber === ProviderResultsTableComponent.RFQ_EC_NUMBER,
    );
    if (!rfqCriteria) {
      return undefined;
    }

    const ec = this.ecs().find(
      (item) => item.ecNumber === ProviderResultsTableComponent.RFQ_EC_NUMBER,
    );
    const translatedLabel =
      this.directusTranslationPipe.transform(rfqCriteria.translations, this.currentLang, '') ?? '';
    const includedSubCriteria = rfqCriteria.includedSubCriteria.map((subCriteria) => ({
      scNumber: subCriteria.scNumber,
      directusId: subCriteria.directusId,
      translatedLabel:
        this.directusTranslationPipe.transform(subCriteria.translations, this.currentLang, '') ??
        '',
      weight: ec?.subCriteria?.find((item) => item.scNumber === subCriteria.scNumber)?.weight,
    }));

    return {
      ecNumber: rfqCriteria.ecNumber,
      directusId: rfqCriteria.directusId,
      translatedLabel,
      weight: ec?.weight,
      includedSubCriteria,
    };
  }

  protected openSubcriteriaAttributeScores(subCriteria: SubCriteriaRow): void {
    this.selectedSubCriteria.set(subCriteria);
    this.subcriteriaAttributeScoresModalVisible.set(true);
  }

  async completePhase() {
    const completePhaseAndRefreshSteps = async () => {
      await firstValueFrom(
        this.participatingToolController.completePhase({
          projectId: this.projectId(),
          phase: this.phase(),
        }),
      );
      await this.stepStateService.loadStepStatuses(this.projectId());
    };

    this.confirmDialogService.confirm({
      headerKey: `project.finishPhase.header`,
      messageKey: `project.finishPhase.message`,
      accept: completePhaseAndRefreshSteps,
    });
  }
}
